import axios from "axios";
import { getDeviceId } from "@/lib/device";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const instance = axios.create({
  baseURL: API,
});

// Inject token + device id on every request
instance.interceptors.request.use((config) => {
  const token = localStorage.getItem("es_access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers["X-Device-Id"] = getDeviceId();
  return config;
});

// Detect single-session replacement and force logout-redirect
instance.interceptors.response.use(
  (resp) => resp,
  (err) => {
    const detail = err?.response?.data?.detail;
    if (err?.response?.status === 401 && detail === "session_replaced") {
      // Clear local auth and force re-login with explanatory toast
      localStorage.removeItem("es_access_token");
      localStorage.removeItem("es_user");
      const url = new URL("/login", window.location.origin);
      url.searchParams.set("session_replaced", "1");
      // Avoid infinite loop if already on login
      if (window.location.pathname !== "/login") {
        window.location.replace(url.toString());
      }
    }
    return Promise.reject(err);
  },
);

export function setToken(token) {
  if (token) localStorage.setItem("es_access_token", token);
  else localStorage.removeItem("es_access_token");
}

export function getToken() {
  return localStorage.getItem("es_access_token");
}

export function formatApiError(detail) {
  if (detail == null) return "An unexpected error occurred. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function fileUrl(idOrPath, opts = {}) {
  if (!idOrPath) return "";
  // Existing absolute URL → leave as-is
  if (/^https?:\/\//.test(idOrPath)) return idOrPath;
  // Already a backend-relative path
  if (idOrPath.startsWith("/api/")) {
    let url = idOrPath;
    // Auto-route bare /api/files/{id} to public for non-sensitive assets
    if (opts.publicAsset && /^\/api\/files\/[^/?]+$/.test(url)) {
      url = `${url}/public`;
    }
    return `${BACKEND_URL}${url}`;
  }
  // Bare file id
  if (opts.publicAsset) return `${API}/files/${idOrPath}/public`;
  return `${API}/files/${idOrPath}`;
}

/** Build a thumbnail URL safely usable from <img> tags (no auth required). */
export function thumbUrl(idOrPath, updatedAt) {
  if (!idOrPath) return "";
  // External URLs (e.g. https://images.unsplash.com/...): pass through
  if (/^https?:\/\//.test(idOrPath) && !idOrPath.includes(BACKEND_URL)) return idOrPath;

  let path = idOrPath;
  // Convert backend file URLs to the public variant
  if (path.startsWith(BACKEND_URL)) path = path.slice(BACKEND_URL.length);
  if (/^\/api\/files\/[^/?]+$/.test(path)) path = `${path}/public`;
  // Bare file id
  if (!path.startsWith("/")) path = `/api/files/${path}/public`;
  // Cache-bust on update
  const sep = path.includes("?") ? "&" : "?";
  const v = updatedAt ? `${sep}v=${encodeURIComponent(updatedAt)}` : "";
  return `${BACKEND_URL}${path}${v}`;
}

export default instance;
